import React, { useState } from 'react';
import styled from 'styled-components';
import CommonMenu from 'src/components/CommonMenu';
import { useNavigate } from 'react-router';
import { Poppins, Spacer, TextOverflowTooltip } from 'src/common';
import { ThreeDots } from 'src/components/CommonMenu/comps';
import { Tile, TileDescription, TileDescriptionContainer, TileTitle } from 'src/components/Tile';
import { Link } from 'react-router-dom';
import { MPEvents } from 'src/utils/mixpanel/types';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { ScenarioSvg } from 'src/assets/dynamicSvg';
import { useComplianceFrameworkGaugeQuery } from 'src/graphql/_generated';
import { ComplienceChart } from 'src/components/charts';
import { useAuth } from 'src/state/auth';
import { isQueryLoading } from 'src/graphql/client';
import PushPinIcon from '@mui/icons-material/PushPin';
import { ControlFrameworkSummary } from '../ControlFrameworkVersionHistory';

const BottomContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const TitleContainer = styled.div`
  min-width: 0;
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
`;

const PinIndicator = styled(PushPinIcon)`
  color: #ff4d3b;
  font-size: 28px;
  transform: rotate(-28deg);
`;

const PrimaryTag = styled.div`
  border: 1px solid #ff4d3b;
  border-radius: 999px;
  padding: 7px 14px;
  min-width: 75px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.white};
  box-sizing: border-box;
`;

interface FrameworkLibraryCardProps {
  framework: ControlFrameworkSummary;
  isPrimary?: boolean;
  isPinned?: boolean;
  isDragEnabled?: boolean;
  onTogglePin?: () => void;
  onChangePrimary?: () => void;
  onDelete?: () => void;
  onDragStart?: React.DragEventHandler<HTMLDivElement>;
  onDragEnd?: React.DragEventHandler<HTMLDivElement>;
  onDragOver?: React.DragEventHandler<HTMLDivElement>;
  onDrop?: React.DragEventHandler<HTMLDivElement>;
}

export const FrameworkLibraryCard: React.FC<FrameworkLibraryCardProps> = ({
  framework,
  isPrimary,
  isPinned,
  isDragEnabled,
  onTogglePin,
  onChangePrimary,
  onDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}) => {
  const navigate = useNavigate();
  const { workspaceId } = useAuth();
  const [anchorEl, setAnchorEl] = useState<React.MouseEvent['currentTarget'] | null>(null);

  const { data, loading, networkStatus } = useComplianceFrameworkGaugeQuery({
    variables: {
      workspaceId,
      id: framework.id,
    },
  });
  const isloading = isQueryLoading({ loading, data, networkStatus });
  const cfData = data?.complianceFrameworks[0];
  const authorLabel = framework.organisation?.name || framework.author || 'Cydea';

  const handleClose = () => setAnchorEl(null);

  const handleOnViewClick = () => navigate(`/control-frameworks/${framework.id}`);
  const onMenuClick = (e: React.MouseEvent<HTMLLIElement>, cb: () => void) => {
    e.stopPropagation();
    handleClose();
    cb();
  };

  return (
    <>
      <CommonMenu
        configuredList={[
          {
            type: 'VIEW',
            exe: (e) => onMenuClick(e, () => handleOnViewClick()),
          },
          isPrimary && onChangePrimary
            ? {
                type: 'CHANGE_PRIMARY_FRAMEWORK',
                label: 'Change primary framework',
                exe: (e) => onMenuClick(e, () => onChangePrimary()),
              }
            : false,
          !isPrimary && onTogglePin
            ? {
                type: 'PIN',
                label: isPinned ? 'Unpin' : 'Pin',
                exe: (e) => onMenuClick(e, () => onTogglePin()),
              }
            : false,
          onDelete
            ? {
                type: 'DELETE',
                label: 'Delete',
                exe: (e) => onMenuClick(e, () => onDelete()),
              }
            : false,
        ]}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      />

      <Tile
        className="hover-shadow"
        draggable={!!isDragEnabled}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onClick={() => {
          if (!anchorEl) {
            navigate(`/control-frameworks/${framework.id}`);
            mpEvent(MPEvents.Link, {
              source: {
                value: ['Frameworks'],
              },
              destination: { value: ['Framework'], params: { id: framework.id } },
              tags: ['FRAMEWORK'],
            });
          }
        }}
        data-cy="framework-library-card"
      >
        <Head>
          <TitleContainer>
            <TextOverflowTooltip tooltipText={framework.shortname || ''}>
              <Link
                to={`/control-frameworks/${framework.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  mpEvent(MPEvents.Link, {
                    source: {
                      value: ['Frameworks'],
                    },
                    destination: { value: ['Framework'], params: { id: framework.id } },
                    tags: ['FRAMEWORK'],
                  });
                }}
              >
                <TileTitle>{framework.shortname}</TileTitle>
              </Link>
            </TextOverflowTooltip>
          </TitleContainer>
          <Actions>
            {isPrimary ? (
              <PrimaryTag>
                <Poppins px={12} color="prussianBlue">
                  Primary
                </Poppins>
              </PrimaryTag>
            ) : (
              isPinned && <PinIndicator />
            )}
            <ThreeDots
              onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
              }}
            />
          </Actions>
        </Head>
        <Spacer $px={10} />
        <TileDescriptionContainer>
          <TileDescription>
            <Poppins px={14} color="cflowerBlue">
              {framework.name}
            </Poppins>
          </TileDescription>
        </TileDescriptionContainer>

        <BottomContainer>
          <div css="display: flex;align-items: center;flex: 1;">
            <ScenarioSvg />
            <div>
              <Poppins css="margin-left: 6px;" px={14}>
                {authorLabel} •{' '}
              </Poppins>
              <Poppins px={14} weight={600}>
                {framework.controlCount}{' '}
              </Poppins>
              <Poppins px={14}>{framework.controlCount === 1 ? 'control' : 'controls'}</Poppins>
            </div>
          </div>
          <ComplienceChart
            controlsCount={cfData?.controlsCount ?? 0}
            controlsImplementedCount={cfData?.controlsImplementedCount ?? 0}
            controlsPlannedCount={cfData?.controlsPlannedCount ?? 0}
            isloading={isloading}
          />
        </BottomContainer>
      </Tile>
    </>
  );
};
